import React, { useMemo, useState } from "react";
import EmptyTableIconV2 from "../../images/EmptyTableV2";
import LoadingV2 from "../../components/LoadingV2";
import {
  BORDER_SOFT,
  BORDER_STRONG,
  BottomSheet,
  Icon,
  MobileCard,
  MobileCardList,
  SegmentedControl,
  SheetField,
  StickyFooter,
  TAP,
  CONTROL,
  TEAL,
  TEAL_TINT,
  TEXT,
  TEXT_DIM,
  TEXT_MUTED,
  t,
} from "../../components/mobile";

/** One row of getSockHolding's dataStock, as the API returns it. */
export interface StockRow {
  id: string;
  Supplier: string | null;
  sku: string;
  product: string;
  uom: string;
  cost: string | number;
  quantity: string | number;
  linecost: number;
  inuse: boolean;
}

interface Props {
  language: any;
  total: any;
  rows: StockRow[];
  count: number;
  page: number;
  limit: number;
  loading: boolean;
  locations: { id: string; name: string }[];
  /** The page's filter state (searchProductName, searchInUse, location). */
  filter: any;
  onSearchChange: (field: string, value: string) => void;
  /** The existing handleSelectLocation: a location id, or "location" for all. */
  onSelectLocation: (id: string) => void;
  onPageChange: (page: number) => void;
  searchInputRef: React.MutableRefObject<HTMLInputElement | null>;
  emptyState: { title?: string; desc?: string };
}

const SUMMARY_KEY = "inventory.kpiExpanded";

const formatNumber = (value: any, fractionDigits = 0): string =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(Number(value) || 0);

const formatMoney = (value: any): string => `${formatNumber(value, 2)} €`;

/**
 * The Inventory screen on phones: one scroll surface; title, a collapsible
 * KPI summary, a sticky search + Filters row, stock rows as cards with a
 * record sheet, Prev/Next paging. Same getSockHolding params as desktop.
 */
const InventoryMobile: React.FC<Props> = ({
  language,
  total,
  rows,
  count,
  page,
  limit,
  loading,
  locations,
  filter,
  onSearchChange,
  onSelectLocation,
  onPageChange,
  searchInputRef,
  emptyState,
}) => {
  const [kpiOpen, setKpiOpen] = useState<boolean>(
    () => sessionStorage.getItem(SUMMARY_KEY) === "1",
  );
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [recordId, setRecordId] = useState<string>("");
  /** Pinned search inside the Location list (shown once the list is long enough to need it). */
  const [locationQuery, setLocationQuery] = useState<string>("");
  const visibleLocations = locationQuery.trim()
    ? locations.filter((loc) => String(loc.name ?? "").toLowerCase().includes(locationQuery.trim().toLowerCase()))
    : locations;

  const search: string = filter?.searchProductName ?? "";
  const inUse: string = filter?.searchInUse ?? "";
  const locationId: string = filter?.location ?? "";
  const activeLocation = locations.find((l) => l.id === locationId);
  const activeFilterCount = (locationId ? 1 : 0) + (inUse ? 1 : 0);
  const hasQuery = !!search.trim() || activeFilterCount > 0;
  const totalPages = Math.max(1, Math.ceil((count || 0) / (limit || 25)));
  const record = useMemo(() => rows.find((r) => r.id === recordId), [rows, recordId]);

  const toggleKpi = () => {
    setKpiOpen((open) => {
      sessionStorage.setItem(SUMMARY_KEY, open ? "0" : "1");
      return !open;
    });
  };

  const kpis = [
    { label: t(language, "Inventory Valuation"), value: formatMoney(total?.totalInventoryValuation) },
    { label: t(language, "Total Unique Items"), value: formatNumber(total?.totalUniqueItems) },
    { label: t(language, "Total Items"), value: formatNumber(total?.totalItems) },
  ];

  const inUseLabel = (v: boolean) => (v ? t(language, "Yes") : t(language, "No"));

  const pageButton = (label: string, disabled: boolean, onClick: () => void, flip?: boolean) => (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="flex items-center justify-center"
      style={{
        width: `${TAP}px`,
        height: `${TAP}px`,
        borderRadius: "12px",
        border: `1px solid ${BORDER_STRONG}`,
        backgroundColor: "#FFFFFF",
        opacity: disabled ? 0.4 : 1,
        transform: flip ? "rotate(180deg)" : undefined,
        cursor: disabled ? "default" : "pointer",
      }}
    >
      <Icon name="chev" size={16} color={TEXT} />
    </button>
  );

  const radioRow = (label: string, selected: boolean, onClick: () => void, key: string, last?: boolean) => (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      key={key}
      onClick={onClick}
      className="flex items-center font-inter"
      style={{
        width: "100%",
        minHeight: "48px",
        padding: "6px 20px",
        gap: "12px",
        textAlign: "left",
        border: "none",
        borderBottom: last ? undefined : `1px solid ${BORDER_SOFT}`,
        backgroundColor: selected ? TEAL_TINT : "transparent",
        cursor: "pointer",
      }}
    >
      <span
        style={{
          width: "20px",
          height: "20px",
          borderRadius: "999px",
          flex: "none",
          border: `${selected ? 6 : 1.5}px solid ${selected ? TEAL : BORDER_STRONG}`,
          boxSizing: "border-box",
          backgroundColor: "#FFFFFF",
        }}
      />
      <span style={{ fontSize: "15px", fontWeight: selected ? 500 : 400, color: TEXT, flex: 1, minWidth: 0 }}>
        {label}
      </span>
    </button>
  );

  return (
    <div
      className="flex flex-col h-full min-h-0"
      style={{ position: "relative" }}
    >
      {/* The page's single scroll surface. */}
      <div
        className="scroll-container"
        style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch" }}
      >
        <p
          style={{ color: TEXT, fontSize: "22px", lineHeight: "28px" }}
          className="font-semibold font-inter"
        >
          {t(language, "Current Inventory")}
        </p>

        {/* KPI strip: one summary row, chevron expands to the three cards. */}
        <div style={{ marginTop: "10px", border: `1px solid ${BORDER_SOFT}`, borderRadius: "12px" }}>
          <button
            type="button"
            aria-expanded={kpiOpen}
            onClick={toggleKpi}
            className="flex items-center font-inter"
            style={{
              width: "100%",
              minHeight: `${TAP}px`,
              padding: "6px 12px",
              gap: "8px",
              border: "none",
              background: "transparent",
              textAlign: "left",
              cursor: "pointer",
            }}
          >
            <span
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: "14px",
                color: TEXT,
                fontVariantNumeric: "tabular-nums",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              <span style={{ fontWeight: 600 }}>{kpis[0].value}</span>
              <span style={{ color: TEXT_DIM }}> · </span>
              {kpis[1].value} {t(language, "SKUs")}
              <span style={{ color: TEXT_DIM }}> · </span>
              {kpis[2].value} {t(language, "units")}
            </span>
            <span style={{ transform: kpiOpen ? "rotate(180deg)" : undefined, display: "flex" }}>
              <Icon name="chevd" size={18} />
            </span>
          </button>
          {kpiOpen && (
            <div className="flex flex-col" style={{ gap: "8px", padding: "0 12px 12px" }}>
              {kpis.map((kpi) => (
                <div
                  key={kpi.label}
                  style={{ border: `1px solid ${BORDER_SOFT}`, padding: "12px 16px", borderRadius: "12px" }}
                >
                  <p className="text-[14px] font-inter" style={{ color: TEXT_MUTED }}>
                    {kpi.label}
                  </p>
                  <p
                    className="text-[24px] font-semibold font-inter"
                    style={{ color: TEXT, fontVariantNumeric: "tabular-nums" }}
                  >
                    {kpi.value}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sticky search + Filters row. */}
        <div
          className="flex items-center"
          style={{
            position: "sticky",
            top: 0,
            zIndex: 5,
            backgroundColor: "#FFFFFF",
            padding: "12px 0 8px",
            gap: "8px",
          }}
        >
          <div
            className="flex items-center"
            style={{
              flex: 1,
              minWidth: 0,
              height: `${CONTROL}px`,
              border: `1px solid ${BORDER_STRONG}`,
              borderRadius: "12px",
              gap: "8px",
              padding: "0 12px",
            }}
          >
            <Icon name="search" size={18} />
            <input
              ref={searchInputRef}
              type="search"
              inputMode="search"
              enterKeyHint="search"
              aria-label={t(language, "Search product")}
              value={search}
              onChange={(e) => onSearchChange("searchProductName", e.target.value)}
              placeholder={t(language, "Search product")}
              className="font-inter placeholder:text-placeholder bg-transparent outline-none focus-none"
              style={{ fontSize: "16px", flex: 1, minWidth: 0, color: TEXT, height: "100%", border: "none", WebkitAppearance: "none" }}
            />
            {search && (
              <button
                type="button"
                aria-label={t(language, "Clear search")}
                onClick={() => onSearchChange("searchProductName", "")}
                className="flex items-center justify-center"
                style={{ width: "32px", height: `${CONTROL}px`, border: "none", background: "transparent", marginRight: "-8px", cursor: "pointer" }}
              >
                <Icon name="close" size={16} />
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={() => setFiltersOpen(true)}
            className="flex items-center font-inter"
            style={{
              flex: "none",
              height: `${TAP}px`,
              padding: "0 14px",
              gap: "6px",
              borderRadius: "30px",
              border: `1px solid ${activeFilterCount ? TEAL : BORDER_STRONG}`,
              backgroundColor: activeFilterCount ? TEAL_TINT : "#FFFFFF",
              color: TEAL,
              fontSize: "14px",
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            <Icon name="sliders" color={TEAL} size={18} />
            <span>{t(language, "Filters")}</span>
            {activeFilterCount > 0 && (
              <span
                style={{
                  minWidth: "20px",
                  height: "20px",
                  padding: "0 6px",
                  borderRadius: "999px",
                  backgroundColor: TEAL,
                  color: "#FFFFFF",
                  fontSize: "12px",
                  fontWeight: 600,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        {/* Rows. The loading gate covers only this region. */}
        <div style={{ position: "relative", minHeight: loading ? "240px" : undefined, overflow: loading ? "hidden" : undefined }}>
          {loading ? (
            <LoadingV2 />
          ) : rows.length === 0 ? (
            <div
              className="flex justify-center items-center"
              style={{ flexDirection: "column", backgroundColor: "#F8F8F8", borderRadius: "16px", padding: "24px", marginTop: "8px" }}
            >
              <div className="flex items-center justify-center" style={{ width: "48px", height: "48px" }}>
                <EmptyTableIconV2 />
              </div>
              <div className="space-y-1" style={{ marginTop: "12px", textAlign: "center" }}>
                <p className="font-inter" style={{ fontSize: "14px", fontWeight: 600, lineHeight: "20px", color: TEXT }}>
                  {hasQuery ? t(language, "No Results Found") : emptyState.title}
                </p>
                <p className="font-inter" style={{ fontSize: "14px", lineHeight: "20px", color: TEXT_DIM }}>
                  {hasQuery
                    ? t(language, "Try another search or clear the filters")
                    : emptyState.desc}
                </p>
              </div>
            </div>
          ) : (
            <MobileCardList>
              {rows.map((row, index) => (
                <MobileCard
                  key={row.id}
                  title={row.product || "—"}
                  subtitle={[row.sku, row.Supplier].filter(Boolean).join(" · ") || undefined}
                  subtitle2={row.inuse ? undefined : t(language, "Not in use")}
                  trailing={formatMoney(row.linecost)}
                  trailingSub={`${t(language, "Qty")} ${formatNumber(row.quantity)}${row.uom ? ` ${row.uom}` : ""}`}
                  onClick={() => setRecordId(row.id)}
                  last={index === rows.length - 1}
                />
              ))}
            </MobileCardList>
          )}
        </div>

        {!loading && rows.length > 0 && (
          <div className="flex items-center justify-between" style={{ padding: "12px 0 16px" }}>
            {pageButton(t(language, "Previous"), page <= 1, () => onPageChange(page - 1), true)}
            <span className="font-inter" style={{ fontSize: "13px", color: TEXT_DIM, textAlign: "center" }}>
              {t(language, "Showing")} {(page - 1) * limit + 1}–{Math.min(page * limit, count)} {t(language, "of")}{" "}
              {formatNumber(count)}
            </span>
            {pageButton(t(language, "Next"), page >= totalPages, () => onPageChange(page + 1))}
          </div>
        )}
      </div>

      {/* Filters sheet. */}
      <BottomSheet
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        title={t(language, "Filters")}
        footer={
          <StickyFooter
            position="static"
            cancelLabel={t(language, "Clear all")}
            onCancel={() => {
              // Location first: handleSelectLocation spreads the page's
              // current filter synchronously, while handleChangeSearch is a
              // functional update that then lands on top of it.
              if (locationId) onSelectLocation("location");
              if (inUse) onSearchChange("searchInUse", "");
            }}
            primaryLabel={`${t(language, "Show")} ${formatNumber(count)} ${t(language, "items")}`}
            onPrimary={() => setFiltersOpen(false)}
          />
        }
      >
        <div className="font-inter" style={{ padding: "4px 20px 8px", fontSize: "13px", fontWeight: 500, color: TEXT_MUTED }}>
          {t(language, "In Use")}
        </div>
        <div style={{ padding: "0 20px 12px" }}>
          <SegmentedControl
            ariaLabel={t(language, "In Use")}
            value={inUse || "all"}
            onChange={(v) => onSearchChange("searchInUse", v === "all" ? "" : v)}
            options={[
              { value: "all", label: t(language, "All") },
              { value: "true", label: t(language, "Yes") },
              { value: "false", label: t(language, "No") },
            ]}
          />
        </div>
        <div
          className="font-inter"
          style={{ padding: "8px 20px", fontSize: "13px", fontWeight: 500, color: TEXT_MUTED, borderTop: `1px solid ${BORDER_SOFT}` }}
        >
          {t(language, "Location")}
        </div>
        {locations.length > 6 && (
          <div style={{ padding: "0 20px 8px" }}>
            <div
              className="flex items-center"
              style={{ height: `${CONTROL}px`, border: `1px solid ${BORDER_STRONG}`, borderRadius: "12px", gap: "8px", padding: "0 12px" }}
            >
              <Icon name="search" size={18} />
              <input
                type="search"
                inputMode="search"
                aria-label={t(language, "Search locations")}
                placeholder={t(language, "Search locations")}
                value={locationQuery}
                onChange={(e) => setLocationQuery(e.target.value)}
                className="font-inter"
                style={{ flex: 1, minWidth: 0, border: "none", outline: "none", background: "transparent", fontSize: "16px", color: TEXT }}
              />
            </div>
          </div>
        )}
        <div role="radiogroup" aria-label={t(language, "Location")}>
          {!locationQuery.trim() &&
            radioRow(t(language, "All locations"), !locationId, () => onSelectLocation("location"), "all", visibleLocations.length === 0)}
          {visibleLocations.map((loc, index) =>
            radioRow(loc.name, loc.id === locationId, () => onSelectLocation(loc.id), loc.id, index === visibleLocations.length - 1),
          )}
          {locationQuery.trim() && visibleLocations.length === 0 && (
            <div className="font-inter" style={{ padding: "16px 20px", fontSize: "14px", color: TEXT_MUTED }}>
              {t(language, "No Results Found")}
            </div>
          )}
        </div>
      </BottomSheet>

      {/* Record sheet: every field of the tapped row. */}
      <BottomSheet open={!!record} onClose={() => setRecordId("")}>
        {record && (
          <div style={{ padding: "0 20px 8px" }}>
            <div className="font-inter" style={{ fontSize: "18px", fontWeight: 600, color: TEXT, wordBreak: "break-word" }}>
              {record.product || "—"}
            </div>
            <div className="font-inter" style={{ fontSize: "13px", color: TEXT_DIM, marginTop: "2px" }}>
              {[record.sku, activeLocation?.name].filter(Boolean).join(" · ")}
            </div>
            <div className="flex flex-col" style={{ gap: "4px", marginTop: "14px", paddingTop: "12px", borderTop: `1px solid ${BORDER_SOFT}` }}>
              <SheetField label={t(language, "Supplier")} value={record.Supplier || "/"} />
              <SheetField label={t(language, "SKU")} value={record.sku || "—"} />
              <SheetField label={t(language, "UOM")} value={record.uom || "—"} />
              <SheetField label={t(language, "Qty")} value={formatNumber(record.quantity)} />
              <SheetField label={t(language, "Cost")} value={formatMoney(record.cost)} />
              <SheetField label={t(language, "Line Cost")} value={formatMoney(record.linecost)} />
              <SheetField label={t(language, "In Use")} value={inUseLabel(record.inuse)} />
            </div>
          </div>
        )}
      </BottomSheet>
    </div>
  );
};

export default InventoryMobile;
